import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {MenuItem, MenuItemPush, MenuStructure, PickedPackage, PushPage, PushSelection} from '../../models/FSMenu';
import { MenuService } from '../../services/menu.service';
import { OrderService } from '../../services/order.service';
import { MenuUtils } from '../../utils/Utils';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { TranslateService } from '../../services/translate.service';
import {FormsModule} from '@angular/forms';
import {NgClass, NgForOf, NgIf} from '@angular/common';
import {DialogButtonComponent} from '../buttons/dialog-button.component';
import {SmallActionButtonComponent} from '../buttons/small-action-button.component';
import {PlainButtonComponent} from '../buttons/plain-button.component';
import {ActionButtonComponent} from '../buttons/action-button.component';
import {BottomSheetService} from '../../services/bottom-sheet.service';
import {BoxForCheckboxComponent} from '../ui/box-for-checkbox.component';
import {CheckboxLabelComponent} from '../ui/checkbox-label.component';
import {RadioLabelComponent} from '../ui/radio-label.component';
import {OptionContentComponent} from '../ui/option-content.component';
import {SeparatorComponent} from '../ui/separator.component';
import {PushItemContentComponent} from '../ui/push-item-content.component';

@Component({
  selector: 'app-push',
  standalone: true,
  imports: [
    FormsModule,
    NgForOf,
    NgIf,
    NgClass,
    DialogButtonComponent,
    SmallActionButtonComponent,
    PlainButtonComponent,
    ActionButtonComponent,
    BoxForCheckboxComponent,
    CheckboxLabelComponent,
    RadioLabelComponent,
    OptionContentComponent,
    SeparatorComponent,
    PushItemContentComponent
  ],
  templateUrl: './push.component.html'
})
export class PushComponent implements OnInit, OnDestroy {
  @Input() item!: MenuItem;
  @Input() pushIndex: number = 0;
  @Input() parentRID: string = '';
  // The sheet's page: holds what was ticked while a package's pick sheet is on top
  @Input() page?: PushPage;

  push?: MenuItemPush;
  isWorking = false;
  selectedItems: { [key: string]: boolean } = {};
  attrValues: { [key: string]: any } = {};
  picked: Record<string, PickedPackage> = {};
  itemIncluded = false;
  itemMandatory = false;
  itemSingleNotSelected = false;
  private menu?: MenuStructure;

  private sub?: Subscription;

  constructor(
    private menuService: MenuService,
    private auth: AuthService,
    private orderService: OrderService,
    private translate: TranslateService,
    private sheetService: BottomSheetService
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] || changes['pushIndex']) {
      // Reset state and reinitialize
      this.isWorking = false;
      this.itemIncluded = false;
      this.itemMandatory = false;
      this.itemSingleNotSelected = false;
      this.initializeComponent();
    }
  }

  initializeComponent(): void {
    // Picking a package opens the item view on top, which rebuilds this page when it closes: keep the ticks
    const state: PushSelection = this.page ? (this.page.state ??= {selectedItems: {}, attrValues: {}, picked: {}})
      : {selectedItems: {}, attrValues: {}, picked: {}};
    this.selectedItems = state.selectedItems;
    this.attrValues = state.attrValues;
    this.picked = state.picked;
    const pi = this.item.pushes[this.pushIndex];
    this.push = { index: pi.index, title: pi.title, groups: [], included: false, mandatory: false} as MenuItemPush;

    if (pi.included || pi.mandatory) {
      this.itemIncluded = pi.included;
      this.itemMandatory = pi.mandatory;
      this.itemSingleNotSelected = !Object.values(this.selectedItems).some(v => v);
      this.sheetService.lockSheet();
    }

    const venueId = this.auth.venueId;
    this.sub = this.menuService.observeUnfilteredMenu(venueId!).subscribe(menu => {
      this.push = { index: pi.index, title: pi.title, groups: [], included: false, mandatory: false } as MenuItemPush;
      this.menu = menu;

      for (const group of pi.groups) {
        // Get available items for this group
        const menuItems = this.menuService.getFilteredMenuItemsFromGroup(group, menu);

        console.log('TAG:PSH', menuItems);

        for (const item of menuItems) {
          this.selectedItems[item.id] ??= false;
        }

        if (menuItems.length > 0) {
          this.push.groups.push({ title: group.title, menuItems, items: group.items });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  continue() {
    const itemIds = MenuUtils.collectSelectedItemIds(this.selectedItems, this.attrValues, this.menu, this.picked);
    if (itemIds.length > 0) {
      this.addItemsToOrder(itemIds);
    } else {
      this.next();
    }
  }

  selectedChange(selectedItem: MenuItem) {
    console.log('selectedChange', selectedItem);
    // A package that asks something is ticked once its options are picked in the item view
    if (this.selectedItems[selectedItem.id] && this.needsPicking(selectedItem)) {
      this.selectedItems[selectedItem.id] = false;
      this.openPicks(selectedItem);
      return;
    }
    this.applySelection(selectedItem);
  }

  openPicks(item: MenuItem) {
    this.sheetService.displayPackagePick(item, {
      startId: this.picked[item.id]?.id,
      included: this.itemIncluded,
      done: picked => {
        this.picked[item.id] = picked;
        if (!this.selectedItems[item.id]) {
          this.selectedItems[item.id] = true;
          this.applySelection(item);
        }
      }
    });
  }

  private needsPicking(item: MenuItem): boolean {
    const menu = this.menu;
    return menu != null && MenuUtils.isPackageChangeable(item, id => MenuUtils.getItemInMenu(id, menu));
  }

  private applySelection(selectedItem: MenuItem) {
    if (this.itemIncluded || this.itemMandatory) {
      const cur = this.selectedItems[selectedItem.id];
      for (const key of Object.keys(this.selectedItems)) {
        this.selectedItems[key] = false;
      }
      this.selectedItems[selectedItem.id] = cur;
      this.itemSingleNotSelected = !cur;
    }

    // Setup default attribute values
    this.attrValues[selectedItem.id] = {};
    for (const atr of selectedItem.attributes ?? []) {
      if (!this.attrValues[selectedItem.id][atr.id]) {
        const opt = atr.options.find(o => o.default) ?? (atr.options.length > 1 ? atr.options[0] : undefined);
        if (opt) {
          this.attrValues[selectedItem.id][atr.id] = opt.key;
        }
      }
    }
  }

  addItemsToOrder(itemIds: string[]) {
    this.isWorking = true;
    const inc = this.itemIncluded ? true : undefined;
    const mand = this.itemMandatory ? true : undefined;
    this.orderService.addItemsToOrder(itemIds, this.parentRID, inc, mand).subscribe(() => {
      this.next();
    });
  }

  getString(s: string, postCase = "normal") {
    return this.translate.single(s, postCase);
  }

  getItemPriceFormatted(item: MenuItem) {
    return this.orderService.getPriceForItem(item);
  }

  private next() {
    if (this.pushIndex < this.item.pushes.length - 1) {
      const pp = {item: this.item, pushIndex: this.pushIndex+1, rid:this.parentRID} as PushPage
      this.sheetService.displayPushPage(pp);
    } else {
      this.sheetService.popSheet();
    }
  }
}
